import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface LoginViewProps {
    onLogin: (telegramId: number, password: string) => Promise<void>;
}

export function LoginView({ onLogin }: LoginViewProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [telegramId, setTelegramId] = useState('');
    const [tgPassword, setTgPassword] = useState('');

    const handleTelegramSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        const id = parseInt(telegramId, 10);
        if (isNaN(id)) { setError('Введите корректный Telegram ID (число)'); return; }
        setIsLoading(true);
        try {
            await onLogin(id, tgPassword);
        } catch (err: any) {
            setError(err.response?.data?.detail || err.message || 'Ошибка соединения');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-neutral-950 flex flex-col items-center justify-center p-6">
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="w-full max-w-sm"
            >
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-black bg-gradient-to-r from-green-400 to-emerald-500 bg-clip-text text-transparent">
                        FoodFlow
                    </h1>
                    <p className="text-neutral-500 text-sm mt-2">Умный дневник питания</p>
                </div>

                <AnimatePresence>
                    {error && (
                        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                            className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                            <p className="text-red-400 text-sm text-center">{error}</p>
                        </motion.div>
                    )}
                </AnimatePresence>

                <motion.form initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    onSubmit={handleTelegramSubmit} className="space-y-4">
                    <div>
                        <label className="block text-neutral-400 text-xs font-medium mb-1.5 uppercase tracking-wider">Ваш Telegram ID</label>
                        <input type="text" inputMode="numeric" pattern="[0-9]*" value={telegramId} onChange={e => setTelegramId(e.target.value)}
                            placeholder="Например: 432823154" required
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-neutral-600 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 transition-all" />
                        <p className="text-neutral-600 text-[10px] mt-1">Узнать свой ID — бот @userinfobot</p>
                    </div>
                    <div>
                        <label className="block text-neutral-400 text-xs font-medium mb-1.5 uppercase tracking-wider">Пароль проекта</label>
                        <input type="password" value={tgPassword} onChange={e => setTgPassword(e.target.value)}
                            placeholder="Пароль от куратора" required
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-neutral-600 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 transition-all" />
                    </div>
                    <button type="submit" disabled={isLoading}
                        className="w-full py-3.5 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white font-bold rounded-xl transition-all duration-300 disabled:opacity-50 shadow-lg shadow-emerald-500/20">
                        {isLoading ? '⏳ Вхожу...' : '🤖 Войти'}
                    </button>
                    <div className="mt-4 p-3 bg-white/[0.02] border border-white/5 rounded-xl">
                        <p className="text-neutral-500 text-xs text-center leading-relaxed">
                            💡 Напишите боту <code className="text-emerald-400/80">/web</code> — он выдаст прямую ссылку для входа без пароля.
                        </p>
                    </div>
                </motion.form>
            </motion.div>
        </div>
    );
}
